import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ModalSection from '../ModalSection';
import PromptModel from '../../../../models/PromptModel';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import QuillComponent, { getPlainTextLength } from '../QuillComponent';
import ToggleSwitchMC from '../../../../components/ToggleSwitchMC';

const GeneralParametersModal = ({ agentId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ChatbotIA');
  const TONE_STYLE_MAX_LENGTH = 300;

  const [toneStyleAndResponseFormat, setToneStyleAndResponseFormat] = useState('');
  const [useEmojis, setUseEmojis] = useState(true);
  const [promptId, setPromptId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isOverLimit = getPlainTextLength(toneStyleAndResponseFormat) > TONE_STYLE_MAX_LENGTH;

  useEffect(() => {
    if (agentId) {
      loadToneStyleAndResponseFormat();
    }
  }, [agentId]);

  const toggleGeneralParametersModal = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const loadToneStyleAndResponseFormat = () => {
    if (!agentId) {
      showtoast(t('config.error_loading'));
      setLoading(false);
      return;
    }

    setLoading(true);
    PromptModel.get(agentId, { type: 'tone_style_and_response_format' })
      .then((promptResponse) => {
        const prompts = promptResponse.data || [];
        const tonePrompt = prompts.find(p => p.type === 'tone_style_and_response_format');

        if (tonePrompt) {
          setToneStyleAndResponseFormat(tonePrompt.prompt || '');
          setPromptId(tonePrompt.id);
          if (typeof tonePrompt.config?.use_emojis === 'boolean') {
            setUseEmojis(tonePrompt.config.use_emojis);
          } else {
            setUseEmojis(true);
          }
        } else {
          setToneStyleAndResponseFormat('');
          setPromptId(null);
          setUseEmojis(true);
        }

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        showtoast(err.error || t('config.error_loading'));
      });
  };

  const updateInfo = (value) => {
    setToneStyleAndResponseFormat(value);
  };

  const onSubmit = () => {
    if (!agentId) {
      showtoast(t('config.error_loading'));
      return;
    }

    const params = promptId
      ? {
        prompt: toneStyleAndResponseFormat,
        config: { use_emojis: useEmojis }
      }
      : {
        type: 'tone_style_and_response_format',
        prompt: toneStyleAndResponseFormat,
        config: { use_emojis: useEmojis }
      };

    setSaving(true);

    const savePromise = promptId
      ? PromptModel.update(agentId, promptId, params)
      : PromptModel.save(agentId, params);

    savePromise
      .then((result) => {
        if (!promptId && result?.id) {
          setPromptId(result.id);
        }
        setTimeout(() => {
          showtoast(t('config.save_success'));
          setSaving(false);
          toggleGeneralParametersModal();
        }, 1000);
      })
      .catch((error) => {
        setSaving(false);
        showtoast(error.message || t('config.save_error'));
      });
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t('config.tone_style_and_response_format.modal_title')}
        submitAction={onSubmit}
        submitLabel={t('guides.sidebar_create.update')}
        submitDisabled={saving || loading || isOverLimit}
      >
        <div className="tw-flex tw-flex-col tw-gap-8 tw-pb-2">
          <ModalSection
            title={t('config.tone_style_and_response_format.section_1_title')}
            description={t('config.tone_style_and_response_format.section_1_description')}
            afterContent={(
              loading ? (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
                  <div className="tw-text-gray-500">{t('config.loading')}</div>
                </div>
              ) : (
                <QuillComponent
                  name="tone_style_and_response_format"
                  value={toneStyleAndResponseFormat || ''}
                  onChange={(value) => { updateInfo(value); }}
                  maxLength={TONE_STYLE_MAX_LENGTH}
                  overflowHidden={false}
                  style={{ height: '180px' }}
                />
              )
            )}
          />

          {!loading && (
            <div className="tw-flex tw-flex-col tw-gap-1 tw-pt-2">
              <ToggleSwitchMC
                isOn={useEmojis}
                handleToggle={() => setUseEmojis(!useEmojis)}
                label={t('config.tone_style_and_response_format.emojis_title')}
                labelClassName="!tw-font-semibold"
              />
              <span className="tw-text-gray-950 tw-text-xs tw-pl-[29px]">
                {t('config.tone_style_and_response_format.emojis_description')}
              </span>
            </div>
          )}
        </div>
      </RightModalLayout>
    </div>
  );
};

export default GeneralParametersModal;
